import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Palmtree } from 'lucide-react';
import Seo, { breadcrumbSchema } from '@/components/Seo';
import Breadcrumbs from '@/components/Breadcrumbs';
import Reveal from '@/components/Reveal';
import CTAPanel from '@/components/CTAPanel';
import { AMENITIES, IMAGES } from '@/data/siteData';

const Amenities = () => (
  <>
    <Seo
      title="Provident Palm Vista Amenities | 60+ Amenities"
      description="Provident Palm Vista amenities — 60+ world-class experiences across kids, fitness & wellness, seniors, community and tropical landscape zones at the 16-acre Shilphata development."
      path="/provident-palm-vista-amenities"
      jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Amenities', path: '/provident-palm-vista-amenities' }])]}
    />
    <section className="pt-28 md:pt-36 pb-16 bg-ivory">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ name: 'Amenities' }]} />
        <Reveal>
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-gold">The Amenity Ecosystem</p>
          <h1 className="font-display text-4xl sm:text-5xl font-medium text-palm mt-3 leading-[1.08] tracking-tight">
            Provident Palm Vista Amenities – 60+ Lifestyle Amenities
          </h1>
          <p className="text-[15px] text-ink/75 mt-5 max-w-3xl leading-relaxed">
            The amenity programme at Provident Palm Vista is organised into distinct zones — spaces for the young, avenues for grown-ups and dedicated corners for elders — all stitched together by a tropical landscape of 30 palm varieties. The list below uses the project’s own terminology.
          </p>
        </Reveal>

        <div className="space-y-10 mt-12">
          {AMENITIES.map((g, gi) => (
            <Reveal key={g.cat} delay={0.05}>
              <div className="grid lg:grid-cols-12 gap-6 items-start">
                <div className="lg:col-span-3">
                  <p className="font-display text-5xl text-palm/10 font-medium leading-none">0{gi + 1}</p>
                  <h2 className="font-display text-2xl sm:text-3xl font-medium text-palm mt-1">{g.cat}</h2>
                </div>
                <div className="lg:col-span-9 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {g.items.map((a) => (
                    <div key={a} className="flex items-center gap-2.5 bg-white border border-line rounded-xl px-4 py-3.5 hover:border-gold/40 hover:shadow-md transition-all">
                      <Palmtree className="h-4 w-4 text-gold shrink-0" strokeWidth={1.5} />
                      <span className="text-[13px] font-medium text-ink/80">{a}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-14 relative rounded-3xl overflow-hidden">
          <img src={IMAGES.pool} alt="Swimming pool amenity at Provident Palm Vista Shilphata" className="w-full h-72 md:h-96 object-cover" loading="lazy" width={848} height={566} />
          <div className="absolute inset-0 bg-gradient-to-t from-palm/85 to-transparent flex items-end p-7 md:p-10">
            <div>
              <p className="text-gold text-xs font-bold tracking-[0.25em] uppercase">Signature Zone</p>
              <p className="font-display text-2xl md:text-4xl text-ivory font-medium mt-2">Pool decks, water edges & lounge sit-outs</p>
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-10 flex flex-wrap gap-5">
          <Link to="/1-bhk" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">1 BHK homes <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/2-bhk" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">2 BHK homes <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/gallery" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">View gallery <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/contact" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">Contact us <ArrowRight className="h-4 w-4" /></Link>
        </Reveal>
      </div>
    </section>
    <CTAPanel title="Experience 60+ Amenities — Book a Site Visit" />
  </>
);

export default Amenities;
